import type { StoreActionApi } from 'react-sweet-state';
import type { IEnvironment } from 'relay-runtime';
import type { ActivationId, CloudId } from '@atlassian/jira-shared-types';
import {
	fireIAErrorAnalytics,
	fireOperationalAnalyticsWithContext,
} from '@atlassian/jira-ui-modifications-analytics';
import { getAnalytics } from '@atlassian/jira-ui-modifications-fields-configuration';
import type { FieldMapFromIframe } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common';

import type { LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/constants';
import type { LookupData } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/types';

import { fireIATrackAnalytics } from '../../../../common/utils/track-analytics';

import type { AppId, ChangeId, IssueAdjustmentsState, StoreContainerProps } from '../../types';
import { UiModificationsFieldsValidationError } from '../../utils/errors';
import { setAppErrors } from '../app-errors';
import { bufferChanges, addBufferError } from '../buffer-changes';
import { setError } from '../errors';
import { unregisterFields } from '../register-fields';
import { updateFields } from '../update-fields';

type ProcessIframeChangesPayload = {
	changeId: ChangeId;
	fieldsChangesUnnormalized: FieldMapFromIframe;
	appId: AppId;
	context: Context;
};

type Context = {
	cloudId: CloudId;
	activationId: ActivationId;
	issueId?: string;
	transitionId?: string;
	environment: IEnvironment;
	getAsyncLookupData: (
		fieldsChanges: FieldMapFromIframe,
	) => Promise<Exclude<LookupData, typeof LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY>>;
	getFilteredFieldChangesBasedOnScope: (fieldChanges: FieldMapFromIframe) => FieldMapFromIframe;
	getValidatedFieldsChanges: (fieldsChanges: FieldMapFromIframe) => Promise<FieldMapFromIframe>;
};

export const processIframeChanges =
	({ changeId, fieldsChangesUnnormalized, appId, context }: ProcessIframeChangesPayload) =>
	async (
		{ getState, dispatch }: StoreActionApi<IssueAdjustmentsState>,
		{ viewType, createAnalyticsEvent }: StoreContainerProps,
	) => {
		const { buffer, analyticsData } = getState();
		const {
			cloudId,
			activationId,
			issueId,
			transitionId,
			environment,
			getAsyncLookupData,
			getFilteredFieldChangesBasedOnScope,
			getValidatedFieldsChanges,
		} = context;
		const { issueAdjustmentModulesCount, configuredAppsCount, adjustmentsAPILatency } =
			analyticsData;
		const bufferForChangeId = buffer[changeId] || {};
		const shouldBufferChanges = Object.prototype.hasOwnProperty.call(bufferForChangeId, appId);

		try {
			const fieldsChanges = getFilteredFieldChangesBasedOnScope(fieldsChangesUnnormalized);
			const validatedFieldsChanges = await getValidatedFieldsChanges(fieldsChanges);

			if (shouldBufferChanges) {
				await dispatch(
					bufferChanges({
						changeId,
						appId,
						fieldsChanges,
						context,
					}),
				);

				return;
			}

			const lookupDataFromApi = await getAsyncLookupData(validatedFieldsChanges);

			await dispatch(
				updateFields({
					changeId,
					fieldsChanges,
					lookupDataFromApi,
					tenantContext: { cloudId, activationId },
					viewContext: { issueId, transitionId },
					environment,
					appId,
				}),
			);

			getAnalytics(viewType).stopTTI({
				issueAdjustmentModulesCount,
				adjustmentsAPILatency,
				configuredAppsCount,
				completionStatus: 'success',
				viewType,
				appIds: appId,
			});

			fireIATrackAnalytics(
				createAnalyticsEvent({}),
				'jiraUiModificationsUseSubmit success',
				viewType,
				{
					issueAdjustmentModulesCount,
					configuredAppsCount,
				},
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			dispatch(setError({ errorCode: 'USE_SUBMIT_ERROR_CODE' }));

			if (shouldBufferChanges) {
				dispatch(addBufferError('USE_SUBMIT_ERROR_CODE'));
				dispatch(unregisterFields({ changeId, appId }));
			}

			getAnalytics(viewType).stopTTI({
				issueAdjustmentModulesCount,
				adjustmentsAPILatency,
				configuredAppsCount,
				completionStatus: 'error',
				viewType,
				appIds: appId,
			});

			if (err instanceof UiModificationsFieldsValidationError) {
				fireOperationalAnalyticsWithContext(
					createAnalyticsEvent({}),
					'fieldsChangesValidation failed',
					viewType,
					{ ...err.attributes },
				);

				// Setting app errors from validators to pass them to the onErrorCallback
				dispatch(setAppErrors({ errors: err.attributes.errors, appId }));
				return;
			}
			fireIAErrorAnalytics({
				error: err,
				id: 'useSubmit',
				viewType,
				attributes: {
					errorCause: err?.cause,
				},
				event: createAnalyticsEvent({}),
			});
		} finally {
			// in case of successful addition of changes to the buffer, we don't want to remove loading state on fields yet
			if (!shouldBufferChanges) {
				dispatch(unregisterFields({ changeId, appId }));
			}
		}
	};
